import * as Sentry from "@sentry/react";
import { scrubString, scrubUrl } from "@shared/scrub";

const dsn = import.meta.env.VITE_SENTRY_DSN;
const envSuffix = import.meta.env.VITE_ENV_SUFFIX ?? "DEV";

// The request URL attached to events / transactions carries `tbl_<resourceKey>`
// list-state params, whose values hold operator filter + search PII (NIP /
// company names). `scrubUrl` redacts those values (and other sensitive params).
function scrubRequestUrl(event: Sentry.Event): void {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
}

// Navigation/fetch breadcrumbs carry the URL too (`from`/`to`/`url`), and
// `beforeSend` otherwise never touches them — so an OAuth `code`/`state` or a
// `tbl_*` filter value would still leak there. Breadcrumb URLs are usually
// relative (`/google-callback?code=…`); `scrubUrl` only parses absolute URLs,
// so resolve against the current origin before scrubbing and restore the
// relative form afterwards.
function scrubBreadcrumbUrl(value: string): string {
  if (/^https?:\/\//i.test(value)) return scrubUrl(value);
  const origin = window.location.origin;
  const scrubbed = scrubUrl(origin + (value.startsWith('/') ? value : `/${value}`));
  return scrubbed.startsWith(origin) ? scrubbed.slice(origin.length) : scrubbed;
}

export function scrubBreadcrumbUrls(event: Sentry.Event): void {
  for (const crumb of event.breadcrumbs ?? []) {
    const data = crumb.data as Record<string, unknown> | undefined;
    if (!data) continue;
    for (const field of ['from', 'to', 'url'] as const) {
      const value = data[field];
      if (typeof value === 'string') data[field] = scrubBreadcrumbUrl(value);
    }
  }
}

// Skip Sentry on localhost — `vite dev` and the e2e-ui harness both set
// `import.meta.env.DEV = true`, so we don't ship local errors / replays
// to Sentry. Production builds (`vite build`) keep the integration on.
if (dsn && !import.meta.env.DEV) {
  Sentry.init({
    dsn,
    environment: envSuffix.toLowerCase(),
    release: import.meta.env.VITE_GIT_SHA,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: envSuffix === "PROD" ? 0.1 : 1.0,
    tracePropagationTargets: [/supabase\.co\/functions/],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: envSuffix === "PROD" ? 1.0 : 0,
    beforeSend(event) {
      // Scrub sensitive patterns from exception messages and values
      event.exception?.values?.forEach((ex) => {
        if (ex.value) ex.value = scrubString(ex.value);
      });
      scrubRequestUrl(event);
      scrubBreadcrumbUrls(event);
      return event;
    },
    beforeSendTransaction(event) {
      // Performance transactions also attach the current URL.
      scrubRequestUrl(event);
      return event;
    },
  });
}

export { Sentry };
